/**
 * *Pridať fakt* (PRD-0075 R-C3): a property and its value, where the analyst has it from (free text
 * and/or one of the entity's source documents) and when it holds. The value is pinned: later
 * documents and merges leave it, and what they say instead is shown beside it in the history.
 */
import { Loader2, Pin } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FactField } from './FactField'
import { type AddFactValues, addFactProblems, EMPTY_FACT, type FieldProblem } from './fact-forms'

const T = 'analystFacts'
/** The select's "no document" choice: a select item cannot hold an empty value. */
const NO_DOCUMENT = '__none__'

export interface SourceDocumentOption {
  id: string
  name: string
}

/** What the form offers. */
export interface AddFactChoices {
  /** The keys: the schema's for the type, then the others the entity holds. */
  keys: readonly string[]
  /** The entity's source documents, any of which may be the fact's source. */
  documents: readonly SourceDocumentOption[]
}

export interface AddFactDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  choices: AddFactChoices
  submit: { pending: boolean; error: string | null; onSubmit: (values: AddFactValues) => void }
}

export function AddFactDialog({ open, onOpenChange, choices, submit }: AddFactDialogProps) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pin aria-hidden className="size-4 text-primary" />
            {t(`${T}.dialogs.addFact.title`)}
          </DialogTitle>
          <DialogDescription>{t(`${T}.dialogs.addFact.description`)}</DialogDescription>
        </DialogHeader>
        {/* Mounted only while open: every opening starts empty. */}
        {open && <AddFactForm choices={choices} submit={submit} />}
      </DialogContent>
    </Dialog>
  )
}

function AddFactForm({ choices, submit }: Pick<AddFactDialogProps, 'choices' | 'submit'>) {
  const { keys, documents } = choices
  const { t } = useTranslation()
  const [values, setValues] = useState<AddFactValues>(EMPTY_FACT)
  const [problems, setProblems] = useState<Partial<Record<keyof AddFactValues, FieldProblem>>>({})
  const set = (field: keyof AddFactValues) => (value: string) =>
    setValues(current => ({ ...current, [field]: value }))
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const found = addFactProblems(values)
    setProblems(found)
    if (Object.keys(found).length === 0) submit.onSubmit(values)
  }
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
      <FactField id="fact-key" label={t(`${T}.fields.property`)} problem={problems.key}>
        <Select value={values.key} onValueChange={set('key')}>
          <SelectTrigger id="fact-key" className="w-full">
            <SelectValue placeholder={t(`${T}.fields.propertyPlaceholder`)} />
          </SelectTrigger>
          <SelectContent>
            {keys.map(key => (
              <SelectItem key={key} value={key}>
                {key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FactField>
      <FactField id="fact-value" label={t(`${T}.fields.value`)} problem={problems.value}>
        <Input id="fact-value" value={values.value} onChange={e => set('value')(e.target.value)} />
      </FactField>
      <SourceFields values={values} set={set} documents={documents} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FactField id="fact-from" label={t(`${T}.fields.validFrom`)}>
          <Input
            id="fact-from"
            type="date"
            value={values.validFrom}
            onChange={e => set('validFrom')(e.target.value)}
          />
        </FactField>
        <FactField id="fact-to" label={t(`${T}.fields.validTo`)} problem={problems.validTo}>
          <Input
            id="fact-to"
            type="date"
            value={values.validTo}
            onChange={e => set('validTo')(e.target.value)}
          />
        </FactField>
      </div>
      {submit.error && (
        <p role="alert" className="text-xs text-destructive">
          {submit.error}
        </p>
      )}
      <DialogFooter>
        <Button type="submit" disabled={submit.pending}>
          {submit.pending && <Loader2 aria-hidden className="animate-spin" />}
          {t(`${T}.dialogs.addFact.submit`)}
        </Button>
      </DialogFooter>
    </form>
  )
}

function SourceFields({
  values,
  set,
  documents,
}: {
  values: AddFactValues
  set: (field: keyof AddFactValues) => (value: string) => void
  documents: readonly SourceDocumentOption[]
}) {
  const { t } = useTranslation()
  return (
    <>
      <FactField id="fact-source" label={t(`${T}.fields.sourceText`)}>
        <Input
          id="fact-source"
          value={values.sourceText}
          placeholder={t(`${T}.fields.sourceTextPlaceholder`)}
          onChange={e => set('sourceText')(e.target.value)}
        />
      </FactField>
      {documents.length > 0 && (
        <FactField id="fact-document" label={t(`${T}.fields.sourceDocument`)}>
          <Select
            value={values.sourceDocumentId || NO_DOCUMENT}
            onValueChange={value => set('sourceDocumentId')(value === NO_DOCUMENT ? '' : value)}
          >
            <SelectTrigger id="fact-document" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_DOCUMENT}>{t(`${T}.fields.noDocument`)}</SelectItem>
              {documents.map(document => (
                <SelectItem key={document.id} value={document.id}>
                  {document.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FactField>
      )}
    </>
  )
}
